import {
  listUploadedWeekPeriods,
  loadWeeklyLineChunkRows,
} from "../../salesDataImport/services/salesLineChunksReader";
import { isImportSaleVoucherRow } from "../../../services/salesImportTransactionDataService";
import {
  salesImportBranchDisplayName,
  salesImportBranchName,
  normalizeSalesImportBranchId,
} from "../../salesDataImport/config/salesImportBranchIds";
import { normalizeItemKey, rowToSalesRecord } from "../models/salesRecord";

const WEEK_BATCH_SIZE = 4;

function branchNameFromRow(row) {
  const id = normalizeSalesImportBranchId(row?.branchId ?? row?.BranchID ?? "");
  return salesImportBranchName(id) || salesImportBranchDisplayName(id) || "";
}

/**
 * Load all sale lines from sales_import_months in weekly batches (no per-item queries).
 * @returns {Promise<{ byItem: Map<string, import("../models/salesRecord").SalesRecord[]>, totalRows: number, branches: string[] }>}
 */
export async function loadSalesHistoryGrouped({ onProgress } = {}) {
  const weeks = await listUploadedWeekPeriods();
  const byItem = new Map();
  const branchSet = new Set();
  let totalRows = 0;
  let processedWeeks = 0;

  for (let i = 0; i < weeks.length; i += WEEK_BATCH_SIZE) {
    const batch = weeks.slice(i, i + WEEK_BATCH_SIZE);
    const parts = await Promise.all(
      batch.map((w) =>
        loadWeeklyLineChunkRows(w.year, w.monthIndex, w.weekOfMonth).catch(() => [])
      )
    );

    for (const rows of parts) {
      for (const row of rows) {
        if (!isImportSaleVoucherRow(row)) continue;

        const rec = rowToSalesRecord({
          ...row,
          branchName: branchNameFromRow(row),
        });
        if (!rec) continue;

        totalRows += 1;
        if (rec.branchName) branchSet.add(rec.branchName);

        const key = normalizeItemKey(rec.itemName);
        let list = byItem.get(key);
        if (!list) {
          list = [];
          byItem.set(key, list);
        }
        list.push(rec);
      }
    }

    processedWeeks += batch.length;
    onProgress?.({
      phase: "loading",
      processedWeeks,
      totalWeeks: weeks.length,
      totalRows,
      uniqueItems: byItem.size,
    });
  }

  return {
    byItem,
    totalRows,
    branches: [...branchSet].sort(),
  };
}

/** Resolve Excel branch label to import branch display name. */
export function resolveBranchFilter(branchLabel) {
  if (!branchLabel || branchLabel === "all") return "all";
  const s = String(branchLabel).trim().toLowerCase();
  const map = {
    main: "Main",
    "1": "Main",
    branch: "Branch",
    "10": "Branch",
    spareway: "Spareway",
    "11": "Spareway",
  };
  if (map[s]) return map[s];
  return branchLabel;
}

export function filterRecordsByBranch(records, branchFilter) {
  if (!branchFilter || branchFilter === "all") return records;
  const target = resolveBranchFilter(branchFilter);
  return records.filter(
    (r) =>
      r.branchName === target ||
      r.branchId === target ||
      salesImportBranchName(r.branchId) === target
  );
}
